import Handlebars from 'handlebars';
import { supabase } from '@/lib/supabase';
import { KNOWN_VARIABLE_NAMES } from './campaign-merge';

export interface TemplateVariable {
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean';
  description?: string;
  required?: boolean;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  variables: TemplateVariable[];
  workspace_id: string;
  created_at: string;
  updated_at: string;
  template_tag_relations?: Array<{
    tag_id: string;
    template_tags: {
      id: string;
      name: string;
    };
  }>;
}

export interface TemplateTag {
  id: string;
  name: string;
  template_count: number;
}

export async function fetchTemplatesByTag(workspaceId: string, tagId?: string | null) {
  try {
    if (tagId) {
      // First get template IDs that have the specified tag
      const { data: tagRelations, error: tagError } = await supabase
        .from('template_tag_relations')
        .select('template_id')
        .eq('tag_id', tagId);

      if (tagError) throw tagError;

      // Then fetch templates with those IDs
      const templateIds = tagRelations.map(relation => relation.template_id);
      
      if (templateIds.length === 0) {
        return [];
      }

      const { data: templates, error: templatesError } = await supabase
        .from('email_templates')
        .select(`
          *,
          template_tag_relations (
            tag_id,
            template_tags (
              id,
              name
            )
          )
        `)
        .eq('workspace_id', workspaceId)
        .in('id', templateIds)
        .order('created_at', { ascending: false });

      if (templatesError) throw templatesError;
      return templates || [];
    } else {
      // If no tag filter, fetch all templates
      const { data, error } = await supabase
        .from('email_templates')
        .select(`
          *,
          template_tag_relations (
            tag_id,
            template_tags (
              id,
              name
            )
          )
        `)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  } catch (error) {
    console.error('Error fetching templates by tag:', error);
    throw error;
  }
}

export async function fetchTemplateTags(workspaceId: string) {
  try {
    const { data, error } = await supabase
      .from('template_tag_stats')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('name');
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching template tags:', error);
    throw error;
  }
}

/**
 * Escape stray `{{` openers that aren't valid Handlebars expressions.
 *
 * A "valid" `{{` is followed by one of:
 *   - `{` (triple-stash for raw HTML)
 *   - block / comment / partial markers: `#`, `/`, `!`, `>`, `^`, `&`, `~`
 *   - identifier start: `[a-zA-Z_@]`
 *   - subexpression: `(`
 *   - string literal: `"` or `'`
 *   - number literal: digit
 *
 * Anything else (whitespace, `<`, EOF, etc.) is treated as a literal `{{`
 * — a broken paste, a price expression, etc. — and gets escaped with a
 * leading backslash (`\{{`), which Handlebars renders as a literal `{{`.
 *
 * Stray `}}` (no matching opener) is NOT escaped — Handlebars tolerates
 * loose closers as plain text. Only unmatched openers throw at parse time.
 */
export function sanitizeStrayMustaches(template: string): {
  sanitized: string;
  strayCount: number;
} {
  let strayCount = 0;
  // Match `{{` NOT preceded by `\` (already escaped) and capture the char after.
  // `(?<!\\)` lookbehind keeps us from double-escaping.
  const sanitized = template.replace(
    /(?<!\\)\{\{([\s\S]?)/g,
    (match, nextChar: string) => {
      // Valid opener if next char starts a Handlebars expression
      if (/^[a-zA-Z_@#/!>^&~({"'0-9]$/.test(nextChar)) {
        return match;
      }
      // Stray `{{` — escape the braces, preserve the captured next char.
      strayCount += 1;
      return '\\{{' + nextChar;
    }
  );
  return { sanitized, strayCount };
}

/**
 * Detect variable-like patterns that won't render at send time:
 *
 *  - `unknownDoubleBrace` — `{{var}}` whose name isn't in
 *    KNOWN_VARIABLE_NAMES. The Handlebars parser accepts these (it doesn't
 *    know the runtime variable set), but the backend send-time renderer
 *    leaves unknown tokens UNTOUCHED, so recipients see literal
 *    `{{customer_id}}` in their email. Usually a typo (`{{firstname}}` vs
 *    `{{first_name}}` is fine because both are in the set; `{{name}}` is
 *    not).
 *
 *  - `suspiciousSingleBrace` — `{var}` (single brace) where `var` IS in
 *    KNOWN_VARIABLE_NAMES. Almost certainly a typo for `{{var}}`. The
 *    backend would render this literally. We only flag KNOWN names to
 *    avoid false positives on legitimate `{0}` / `{config}` / etc. inside
 *    template bodies.
 *
 * Case-insensitive matching mirrors the send-time renderer.
 */
export function detectVariableIssues(template: string): {
  unknownDoubleBrace: string[];
  suspiciousSingleBrace: string[];
} {
  const unknownDoubleBrace = new Set<string>();
  const suspiciousSingleBrace = new Set<string>();

  // Double-brace: extract via regex (avoid AST so we catch unknowns BEFORE
  // sanitization/compile would fail). Allow optional whitespace.
  const doubleRe = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = doubleRe.exec(template)) !== null) {
    const name = m[1].toLowerCase();
    if (!KNOWN_VARIABLE_NAMES.has(name)) {
      unknownDoubleBrace.add(m[1]);
    }
  }

  // Single-brace: only flag names that ARE in KNOWN. Strip the surrounding
  // `{...}` substring AND check the next/previous chars aren't `{`/`}` so
  // we don't double-count parts of a `{{var}}`.
  const singleRe = /(^|[^{])\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}(?!\})/g;
  while ((m = singleRe.exec(template)) !== null) {
    const name = m[2].toLowerCase();
    if (KNOWN_VARIABLE_NAMES.has(name)) {
      suspiciousSingleBrace.add(m[2]);
    }
  }

  return {
    unknownDoubleBrace: Array.from(unknownDoubleBrace),
    suspiciousSingleBrace: Array.from(suspiciousSingleBrace),
  };
}

export function validateTemplate(template: string): {
  isValid: boolean;
  error?: string;
  sanitizedTemplate?: string;
  strayCount?: number;
  unknownDoubleBrace?: string[];
  suspiciousSingleBrace?: string[];
} {
  // First, try to clean up stray `{{` that aren't valid expressions.
  // Common cause: paste from a previously-rendered email where merge tags
  // got partially overwritten with HTML.
  const { sanitized, strayCount } = sanitizeStrayMustaches(template);
  const templateToCompile = strayCount > 0 ? sanitized : template;

  const { unknownDoubleBrace, suspiciousSingleBrace } =
    detectVariableIssues(templateToCompile);
  const variableIssueFields = {
    ...(unknownDoubleBrace.length > 0 ? { unknownDoubleBrace } : {}),
    ...(suspiciousSingleBrace.length > 0 ? { suspiciousSingleBrace } : {}),
  };

  try {
    Handlebars.compile(templateToCompile);
    // Extract variables and validate their format
    const variables = extractVariables(templateToCompile);
    for (const variable of variables) {
      if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(variable)) {
        return {
          isValid: false,
          error: `Invalid variable name: ${variable}. Variables must start with a letter and contain only letters and numbers.`,
          ...(strayCount > 0 ? { sanitizedTemplate: sanitized, strayCount } : {}),
          ...variableIssueFields,
        };
      }
    }
    return {
      isValid: true,
      ...(strayCount > 0 ? { sanitizedTemplate: sanitized, strayCount } : {}),
      ...variableIssueFields,
    };
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : 'Invalid template syntax',
      ...(strayCount > 0 ? { sanitizedTemplate: sanitized, strayCount } : {}),
      ...variableIssueFields,
    };
  }
}

export function extractVariables(template: string): string[] {
  const ast = Handlebars.parse(template);
  const variables = new Set<string>();
  const traverse = (node: any) => {
    if (node.type === 'MustacheStatement') {
      const varName = node.path.original;
      if (varName) {
        variables.add(varName);
      }
    }
    if (node.program) {
      node.program.body.forEach(traverse);
    }
  };
  ast.body.forEach(traverse);
  return Array.from(variables);
}

export function renderTemplate(
  template: string, 
  variables: Record<string, any>
): { rendered: string; error?: string } {
  try {
    const compiledTemplate = Handlebars.compile(template);
    return { rendered: compiledTemplate(variables) };
  } catch (error) {
    return { 
      rendered: '', 
      error: error instanceof Error ? error.message : 'Failed to render template' 
    };
  }
}